"use client";

import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import { Icon } from "@iconify/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

// Ce qu'on construit avec Symfony. Accent monochrome (fidèle au logo Symfony).
const DOMAINS = [
    {
        icon: "window-frame-bold-duotone",
        titleKey: "Sf_everywhere_web_title",
        descKey: "Sf_everywhere_web_desc",
    },
    {
        icon: "plug-circle-bold-duotone",
        titleKey: "Sf_everywhere_api_title",
        descKey: "Sf_everywhere_api_desc",
    },
    {
        icon: "cart-large-bold-duotone",
        titleKey: "Sf_everywhere_ecommerce_title",
        descKey: "Sf_everywhere_ecommerce_desc",
    },
    {
        icon: "case-bold-duotone",
        titleKey: "Sf_everywhere_business_title",
        descKey: "Sf_everywhere_business_desc",
    },
    {
        icon: "widget-bold-duotone",
        titleKey: "Sf_everywhere_micro_title",
        descKey: "Sf_everywhere_micro_desc",
    },
    {
        icon: "command-bold-duotone",
        titleKey: "Sf_everywhere_cli_title",
        descKey: "Sf_everywhere_cli_desc",
    },
    {
        icon: "document-text-bold-duotone",
        titleKey: "Sf_everywhere_cms_title",
        descKey: "Sf_everywhere_cms_desc",
    },
    {
        icon: "layers-minimalistic-bold-duotone",
        titleKey: "Sf_everywhere_components_title",
        descKey: "Sf_everywhere_components_desc",
    },
];

// Sites et outils mondialement connus qui tournent avec Symfony ou ses
// composants (vrais logos).
const COMPANIES = [
    { name: "Spotify", logo: "logos:spotify" },
    { name: "Trivago", logo: "simple-icons:trivago" },
    { name: "Dailymotion", logo: "simple-icons:dailymotion" },
    { name: "Laravel", logo: "logos:laravel" },
    { name: "Drupal", logo: "logos:drupal" },
    { name: "Magento", logo: "logos:magento" },
    { name: "Shopware", logo: "simple-icons:shopware" },
    { name: "phpBB", logo: "simple-icons:phpbb" },
];

const gridContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.08, delayChildren: 0.12 } },
};

const cardItem = {
    hidden: { opacity: 0, y: 16, scale: 0.96 },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { duration: 0.45, ease: "easeOut" },
    },
};

export default function SfEverywhere() {
    const t = useTranslations();

    return (
        <section id="symfony-everywhere" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Sf_everywhere_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Sf_everywhere_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10 p-8 sm:p-12">
                {/* Glows monochromes */}
                <div className="absolute -top-24 -right-20 w-[460px] h-[460px] bg-white/10 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-white/5 rounded-full blur-3xl pointer-events-none" />

                <div className="relative">
                    {/* Eyebrow */}
                    <div className="inline-flex items-center gap-2 rounded-full bg-white/10 ring-1 ring-white/20 px-3 py-1 mb-4">
                        <SolarIcon
                            name="global-bold-duotone"
                            className="w-3.5 h-3.5 text-white"
                        />
                        <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white">
                            {t("Sf_everywhere_eyebrow")}
                        </span>
                    </div>

                    {/* Headline */}
                    <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-[1.12] tracking-tight mb-8 max-w-[640px]">
                        {t.rich("Sf_everywhere_headline", {
                            highlight: (chunks) => (
                                <span className="bg-gradient-to-r from-white to-zinc-400 bg-clip-text text-transparent">
                                    {chunks}
                                </span>
                            ),
                        })}
                    </h3>

                    {/* Grille des usages */}
                    <motion.div
                        variants={gridContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-80px" }}
                        className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-5"
                    >
                        {DOMAINS.map((domain) => (
                            <motion.div
                                key={domain.titleKey}
                                variants={cardItem}
                                className="rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-5 hover:bg-white/[0.07] transition-colors duration-200"
                            >
                                <div className="w-11 h-11 rounded-xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center mb-4">
                                    <SolarIcon
                                        name={domain.icon}
                                        className="w-5 h-5 text-white"
                                    />
                                </div>
                                <h4 className="text-base font-semibold text-white mb-1 leading-tight">
                                    {t(domain.titleKey)}
                                </h4>
                                <p className="text-sm text-gray-300/80 leading-relaxed mb-0">
                                    {t(domain.descKey)}
                                </p>
                            </motion.div>
                        ))}
                    </motion.div>

                    {/* Preuve sociale : sites connus + logos */}
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
                        className="mt-8 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 px-5 py-5"
                    >
                        <div className="flex items-start gap-3">
                            <SolarIcon
                                name="verified-check-bold"
                                className="w-5 h-5 text-white shrink-0 mt-0.5"
                            />
                            <p className="text-sm text-gray-300/90 leading-relaxed mb-0">
                                {t.rich("Sf_everywhere_social_proof", {
                                    b: (chunks) => (
                                        <strong className="text-white font-semibold">
                                            {chunks}
                                        </strong>
                                    ),
                                })}
                            </p>
                        </div>
                        <div className="mt-4 flex flex-wrap items-center gap-2.5">
                            {COMPANIES.map((company) => (
                                <span
                                    key={company.name}
                                    title={company.name}
                                    className="inline-flex items-center justify-center h-9 w-12 rounded-md bg-white ring-1 ring-black/5"
                                >
                                    <Icon
                                        icon={company.logo}
                                        className="w-[18px] h-[18px]"
                                        aria-label={company.name}
                                    />
                                </span>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
